/**
 * 大阪府 名物めし 12段階（コンテンツデータ）。
 *
 * 数値（radius / mass / score / unlockScore）は他県と同じテンプレート。
 * 12品すべてが農水省「うちの郷土料理」大阪府の30品から採ってある。
 *
 * お好み焼きについて。
 *  広島（Lv10 広島お好み焼き）、岡山（Lv11 日生カキオコ）に続く3品目。
 *  農水省が大阪のものを「生地に具材を混ぜる『混ぜ焼き』が特徴」と断定形で書いているので、
 *  その工程を文章の主題にする。**他県と比べる文章は書かない。** 形の違いは絵だけで示す。
 *  絵では、切った断面が生地とキャベツの均一なひとつの生地になっていること（層でも麺の上でもない）を
 *  明示的に書いて分けている。
 *
 * 箱寿司について。
 *  押し寿司・ばら寿司は他県で6品使っている。大阪のものは
 *  「一つの木枠に押したものを一口大に切り分けて箱に並べる」「一切れごとに具が違う」という点で
 *  他の6品と形が違う。四種の具が色違いで整列する絵は、どれとも重ならない。
 *  バッテラは〆鯖と白板昆布の棒を切っただけなので、島根の角ずしや長崎の大村寿司と近い。使わない。
 *
 * 形のばらつき。
 *  平たい薄片（紅しょうが天）／細長い小魚（がっちょ）／低く広がる粒（くるみ餅）／
 *  棒状が一方向に絡む（若ごぼう）／手で縦に裂いた房（水なす）／球が一列（たこ焼き）／
 *  角切りの塊（どて焼き）／縦に立つ器（小田巻き蒸し）／串が束になって立つ（串カツ）／
 *  四角い直方体が整列（箱寿司）／大きな円盤（お好み焼き）。
 *  器を描く例外は小田巻き蒸し（蓋つきの茶碗）ときつねうどん（丼）の2つ。
 *
 * 調査で出てきた「書いてはいけないこと」を反映している。
 *  - 道頓堀の看板・ネオン・立体看板は絵にも文章にも出さない。そもそも道頓堀を採っていない。
 *  - 万博記念公園の太陽の塔は岡本太郎の著作物。絵にも文章にも出さない。日本庭園として扱う。
 *  - 通天閣の周囲の企業広告・ロゴ・文字は一切描かせない。
 *  - 鯨のハリハリ鍋は農水省に掲載があるが、配慮のため採らない。
 *  - 泉だこは兵庫の明石だこ、岡山のたこめしと衝突する。たこ焼きでタコ枠を使うので出さない。
 *
 * 本番画像は public/assets/foods/osaka/ に置く。
 *
 * 2026-10-02 確定案（audit/_確定案.csv）で料理を入れ替えた。
 * - 外した品：がっちょの唐揚げ・若ごぼうの炒め煮・小田巻き蒸し（店で食べられる品に差し替え）
 * - かすうどん：「南河内発祥」とは書かない。公式で確かめられていない
 * - かすうどん：油かすをうどんにのせる・だしに脂が溶け出す、といった食べ方は書かない。開いた公式ページ（羽曳野市）に、うどんとしての作り方・見た目の説明がない
 * - かすうどん：「高たんぱく・低脂肪」「1883年に食肉加工場ができた」などは検索結果の要約やニュース・PR記事にしかなく、公式で確かめられないので書かない
 * - 肉吸い：発祥の店名・千日前という場所は書かない。開いた公式ページに書かれていない
 * - 肉吸い：半熟玉子・ねぎ・豆腐は書かない。出典に書かれていない（絵には玉子とねぎがある）
 * - 肉吸い：生まれた年（1980年代など）は書かない。公式で確かめられない
 * - てっちり（ふぐ鍋）：江戸時代にふぐが「御法度」だった話、「大阪の反権威・反骨精神が育んだ味」は書かない。観光局の読み物的な表現で、事実として言い切れない
 * - てっちり（ふぐ鍋）：具（豆腐・白菜・春菊）は書かない。出典にない
 */

import { attachHulls } from '../../utils/itemShape';
import type { RewardLink, StageDefinition, StageItemDefinition } from '../types';

import { osakaSceneryTour } from './osakaScenes';
import { OSAKA_FOOD_HULLS } from './osakaFoodShapes.generated';

/** 「食べに行く」導線の既定リンク（特定店の宣伝にならないよう検索リンクにする）。 */
function searchLinks(area: string, dish: string): RewardLink[] {
  return [
    {
      label: '地図で探す',
      url: `https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(`${area} ${dish}`)}`,
    },
    {
      label: 'お店を調べる',
      url: `https://www.google.com/search?q=${encodeURIComponent(`${area} ${dish}`)}`,
    },
  ];
}

const FOOD_ITEMS: StageItemDefinition[] = [
  {
    id: 'benishoga-ten',
    level: 1,
    name: '紅しょうが天',
    shortName: '紅しょうが天',
    image: '/assets/foods/osaka/01_benishoga_ten.webp',
    radius: 17,
    mass: 1,
    score: 2,
    description: '衣の向こうで、梅酢の赤が透ける。',
    source:
      'https://www.maff.go.jp/j/keikaku/syokubunka/k_ryouri/search_menu/menu/39_30_osaka.html',
    icon: { label: '紅しょうが天', shape: 'dumpling', baseColor: '#d8a848', accentColor: '#d83a5a' },
    reward: {
      unlockScore: 200,
      area: '大阪府内全域',
      headline: '梅酢に漬けた紅しょうがに衣をつけて揚げた、惣菜店の天ぷらコーナーに並ぶ赤い揚げもの。',
      tip: '紅しょうがは、梅干しを漬けたあとの梅酢にしょうがの根茎を漬けてつくる。薄く切って衣をつけ、180度の油で1分ほど揚げる。',
      links: searchLinks('大阪府', '紅しょうが天'),
    },
  },
  {
    id: 'kurumi-mochi',
    level: 2,
    name: 'くるみ餅',
    shortName: 'くるみ餅',
    image: '/assets/foods/osaka/02_kurumi_mochi.webp',
    radius: 21,
    mass: 1.3,
    score: 4,
    description: 'うぐいす色の餡に、くるまれて。',
    source:
      'https://www.maff.go.jp/j/keikaku/syokubunka/k_ryouri/search_menu/menu/39_12_osaka.html',
    icon: { label: 'くるみ餅', shape: 'mochi', baseColor: '#a8c878', accentColor: '#f4f4ec' },
    reward: {
      unlockScore: 500,
      area: '堺市',
      headline: 'くるみは入っていない。すりつぶした枝豆の餡で小さな餅を「くるむ」ことから、その名がついた堺の菓子。',
      tip: '蒸したもち米をついて小さく丸め、すりつぶした枝豆や大豆の餡でからめる。堺が海外貿易で栄えた戦国期に始まったと伝わり、茶菓子として親しまれてきた。',
      links: searchLinks('堺市', 'くるみ餅'),
    },
  },
  {
    id: 'mizunasu',
    level: 3,
    name: '泉州水なすの浅漬',
    shortName: '水なす浅漬',
    image: '/assets/foods/osaka/03_mizunasu.webp',
    radius: 26,
    mass: 1.7,
    score: 8,
    description: '手で縦に裂くと、果汁がしたたる。',
    source:
      'https://www.maff.go.jp/j/keikaku/syokubunka/k_ryouri/search_menu/menu/39_1_osaka.html',
    icon: { label: '水なす浅漬', shape: 'berry', baseColor: '#3a2048', accentColor: '#f0eee0' },
    reward: {
      unlockScore: 1000,
      area: '泉州地域',
      headline: 'ぎゅっと絞ると水がしたたるほど水気が多く、皮のやわらかい丸いなすを、ぬか床に漬けたもの。',
      tip: '泉州水なすは江戸時代初期から泉州でつくられてきた品種で、皮が薄くアクが少なく甘みがある。食べるときはヘタを落として切り込みを入れ、手で縦に割く。',
      links: searchLinks('泉州', '水なす漬'),
    },
  },
  {
    id: 'takoyaki',
    level: 4,
    name: 'たこ焼き',
    shortName: 'たこ焼き',
    image: '/assets/foods/osaka/04_takoyaki.webp',
    radius: 30,
    mass: 2.2,
    score: 16,
    description: '焼きたてのまんまるを、ひとくちで。',
    source:
      'https://www.maff.go.jp/j/keikaku/syokubunka/k_ryouri/search_menu/menu/39_15_osaka.html',
    icon: { label: 'たこ焼き', shape: 'dumpling', baseColor: '#c88a40', accentColor: '#4a3020' },
    reward: {
      unlockScore: 1800,
      area: '大阪市',
      headline: 'だしで溶いた生地にゆでだこと天かすを入れて丸く焼き、濃いソースと青のり、削り節をかけた軽食。',
      tip: '明治末から大正の縁日にあった駄菓子「ちょぼ焼き」、大正末の「ラジオ焼き」を経て、昭和初期に大阪市内でタコを具にした形が生まれた。戦後に濃厚ソースが登場して今の食べ方が定まった。',
      links: searchLinks('大阪市', 'たこ焼き'),
    },
  },
  {
    id: 'dote-yaki',
    level: 5,
    name: 'どて焼き',
    shortName: 'どて焼き',
    image: '/assets/foods/osaka/05_dote_yaki.webp',
    radius: 36,
    mass: 2.8,
    score: 32,
    description: '鍋のふちに盛った、味噌の土手。',
    source:
      'https://www.maff.go.jp/j/keikaku/syokubunka/k_ryouri/search_menu/menu/39_23_osaka.html',
    icon: { label: 'どて焼き', shape: 'pot', baseColor: '#8a4028', accentColor: '#c8703a' },
    reward: {
      unlockScore: 3000,
      area: '大阪市',
      headline: '牛すじ肉を味噌とみりんで長い時間かけて煮込んだ、下町で親しまれてきた一皿。',
      tip: '鉄鍋のふちの内側に味噌を土手のように盛り、鍋の中央で牛すじを焼きながら、溶けてきた味噌で煮込んでいく。名前はこの「土手」に由来する。',
      links: searchLinks('大阪市', 'どて焼き'),
    },
  },
  {
    id: 'kasu-udon',
    level: 6,
    name: 'かすうどん',
    shortName: 'かすうどん',
    image: '/assets/foods/osaka/06_kasu_udon.webp',
    radius: 42,
    mass: 3.5,
    score: 64,
    description: '牛の腸を油でじっくり揚げた「油かす」を使う。油かすは「肉のまち」羽曳野を含む南河内地域で郷土の味として愛されてきた食材で、料理に加えるとコクと旨みが増すとされる。',
    source:
      'https://www.city.habikino.lg.jp/soshiki/toshimiryoku/miryoku/news/14763.html',
    icon: { label: 'かすうどん', shape: 'noodle-bowl', baseColor: '#d8b878', accentColor: '#8a5a28' },
    reward: {
      unlockScore: 4500,
      area: '羽曳野市ほか南河内地域',
      headline: '南河内の郷土の味、油かす',
      tip: '在オーストリア日本国大使館のフェイスブック「郷土料理シリーズ」第6回で、羽曳野市の「かすうどん」が紹介された。',
      links: searchLinks('大阪', 'かすうどん'),
      official: [{ label: '羽曳野市「在オーストリア日本国大使館facebookで『かすうどん』が紹介」', url: 'https://www.city.habikino.lg.jp/soshiki/shiminjinken/shimin_kyodo/news/11318.html' }, { label: '羽曳野市「油かす＆コロッケMAP」', url: 'https://www.city.habikino.lg.jp/soshiki/toshimiryoku/miryoku/map/13773.html' }],
    },
  },
  {
    id: 'nikusui',
    level: 7,
    name: '肉吸い',
    shortName: '肉吸い',
    image: '/assets/foods/osaka/07_nikusui.webp',
    radius: 46,
    mass: 4.4,
    score: 128,
    description: '肉うどんから、うどんを抜いた一杯。鰹と昆布でとったやさしい関西の出汁に、ほろほろと柔らかく煮た牛肉の旨みが溶け出す。大阪のソウルフードと呼ばれる。',
    source:
      'https://www.pref.osaka.lg.jp/o120110/ryutai/meihin/r6new.html',
    icon: { label: '肉吸い', shape: 'noodle-bowl', baseColor: '#e8c878', accentColor: '#a84a3a' },
    reward: {
      unlockScore: 6500,
      area: '大阪市',
      headline: '肉うどんの、うどん抜き',
      tip: '吉本の芸人が「肉うどん、うどん抜きで」と注文したことから生まれた、と紹介されている。',
      links: searchLinks('大阪 千日前', '肉吸い'),
      official: [{ label: '大阪府「大阪産(もん)名品（大阪寿司・惣菜類・加工肉類・パン）」', url: 'https://www.pref.osaka.lg.jp/o120110/ryutai/meihin/sozai.html' }],
    },
  },
  {
    id: 'kushikatsu',
    level: 8,
    name: '串カツ',
    shortName: '串カツ',
    image: '/assets/foods/osaka/08_kushikatsu.webp',
    radius: 51,
    mass: 5.4,
    score: 256,
    description: '串の一本ごとに、揚げたてがある。',
    source:
      'https://www.maff.go.jp/j/keikaku/syokubunka/k_ryouri/search_menu/menu/39_28_osaka.html',
    icon: { label: '串カツ', shape: 'dumpling', baseColor: '#d0a058', accentColor: '#8a6030' },
    reward: {
      unlockScore: 9000,
      area: '大阪市',
      headline: '牛肉や野菜、魚介をひとつずつ串に刺し、衣をつけてパン粉で揚げた、新世界で生まれた名物。',
      tip: '大正末から昭和初めに新世界で生まれたとされ、働く人が安く腹いっぱい食べられるように考えられた。小麦粉と卵の生地をつけ、パン粉をまぶして180度で揚げる。ソースの二度づけをしない。',
      links: searchLinks('大阪市', '串カツ'),
    },
  },
  {
    id: 'kitsune-udon',
    level: 9,
    name: 'きつねうどん',
    shortName: 'きつねうどん',
    image: '/assets/foods/osaka/09_kitsune_udon.webp',
    radius: 57,
    mass: 6.6,
    score: 512,
    description: 'のっているのは、大きいのが一枚。',
    source:
      'https://www.maff.go.jp/j/keikaku/syokubunka/k_ryouri/search_menu/menu/39_17_osaka.html',
    icon: { label: 'きつねうどん', shape: 'noodle-bowl', baseColor: '#e8dcc0', accentColor: '#9a6028' },
    reward: {
      unlockScore: 12000,
      area: '大阪府内全域（大阪市船場で発祥）',
      headline: '甘辛く煮た大きな油揚げを一枚だけのせた、昆布だしのうどん。明治の船場のうどん店から始まった。',
      tip: '明治26年、船場のうどん店で、添え物に出していた油揚げを客が素うどんにのせて食べたのが始まりと伝わる。真昆布と雑節でひいただしに、油抜きして甘辛く煮た油揚げをのせる。',
      links: searchLinks('大阪市', 'きつねうどん'),
    },
  },
  {
    id: 'okonomiyaki',
    level: 10,
    name: 'お好み焼き',
    shortName: 'お好み焼き',
    image: '/assets/foods/osaka/10_okonomiyaki.webp',
    radius: 63,
    mass: 8,
    score: 1024,
    description: 'はじめに混ぜて、それから焼く。',
    source:
      'https://www.maff.go.jp/j/keikaku/syokubunka/k_ryouri/search_menu/menu/39_20_osaka.html',
    icon: { label: 'お好み焼き', shape: 'feast', baseColor: '#a85a30', accentColor: '#e8e0d0' },
    reward: {
      unlockScore: 16000,
      area: '大阪府内全域',
      headline: 'だしで溶いた生地にキャベツと卵、天かすを最初から混ぜ合わせ、鉄板で丸く焼き上げる粉もの。',
      tip: 'ボウルで生地と刻んだキャベツ、卵、天かすを合わせてから鉄板に広げるのが特徴で、これを「混ぜ焼き」と呼ぶ。豚バラ肉をのせて返し、ソース、青のり、削り節をかける。',
      links: searchLinks('大阪市', 'お好み焼き'),
    },
  },
  {
    id: 'hako-zushi',
    level: 11,
    name: '大阪寿司（箱寿司）',
    shortName: '大阪の箱寿司',
    image: '/assets/foods/osaka/11_hako_zushi.webp',
    radius: 70,
    mass: 9.6,
    score: 2048,
    description: '押して切りそろえた、四角い懐石。',
    source:
      'https://www.maff.go.jp/j/keikaku/syokubunka/k_ryouri/search_menu/menu/39_14_osaka.html',
    icon: { label: '大阪の箱寿司', shape: 'roe', baseColor: '#f0e8d8', accentColor: '#e8c038' },
    reward: {
      unlockScore: 22000,
      area: '大阪市',
      headline: '木枠に酢飯と具を重ねて強く押し、一口大に切り分けて彩りよく並べた、大阪のもてなしの寿司。',
      tip: '明治期に船場の寿司店が、鯛やえび、あなごなど上等な具で箱寿司を考案し、「二寸六分の懐石」と呼ばれた。木枠で押すと空気が抜けるので日もちがよく、時間がたつほど味がなじむ。',
      links: searchLinks('大阪市', '箱寿司 大阪寿司'),
    },
  },
  {
    id: 'tecchiri',
    level: 12,
    name: 'てっちり（ふぐ鍋）',
    shortName: 'てっちり',
    image: '/assets/foods/osaka/12_tecchiri.webp',
    radius: 78,
    mass: 11.4,
    score: 4096,
    description: 'ふぐを使った鍋料理で、大阪の冬の風物詩ともされる。ふぐの淡く上品な甘さと独特のコリコリとした食感を、ポン酢と合わせて味わう。',
    source:
      'https://osaka-info.jp/osaka/food/gastronomy-ponzu/',
    icon: { label: 'てっちり', shape: 'hotpot', baseColor: '#f0ece4', accentColor: '#4a7a3a' },
    reward: {
      unlockScore: 30000,
      area: '大阪市',
      headline: 'ふぐをポン酢で食べる冬の鍋',
      tip: '当たれば死ぬことを鉄砲になぞらえ、ふぐを「てつ」の隠語で呼んで楽しんできた。ふぐは全水揚げ量の約60％が大阪で消費されているとされる。',
      links: searchLinks('大阪市', 'てっちり'),
    },
  },
];

/** 得点は 2 ^ level を基準にしている（GAME_TUNING.score で方式変更可能）。 */
export const osakaFoodStage: StageDefinition = {
  regionId: 'osaka',
  regionName: '大阪府',
  mode: 'food',
  title: '大阪めしマージ',
  theme: {
    primaryColor: '#c8402a',
    secondaryColor: '#e8a828',
    accentColor: '#f4c850',
    backgroundColor: '#1a1210',
  },
  /** 背景ツアー「大阪めぐり」。軸は一日の時間。冬の早朝の住吉大社から、夜の新世界へ。 */
  scenery: {
    tour: osakaSceneryTour,
  },
  items: attachHulls(FOOD_ITEMS, OSAKA_FOOD_HULLS),
};
